import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Bar, Line } from 'react-chartjs-2';
import { CircularProgress } from '@material-ui/core';
import { Table, Card, Row, Col, Select, Statistic } from 'antd';
import { StarOutlined, TrophyOutlined, TeamOutlined } from '@ant-design/icons';
import { fetchPOIRatings, fetchPOIRatingById, fetchPois } from 'state/actions/pois';
import {
  ChartTitle,
  ChartWrapper,
  ReportWrapper,
} from 'components/Report/styles';
import { generateFrenchShades } from 'components/Report/utils';

const { Option } = Select;

const POIRatingChart = ({ siteId }) => {
  const dispatch = useDispatch();
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });

  // Define consistent chart color
  const chartColor = generateFrenchShades(3)[1];

  const { ratings, poiRating, loading, poisList, totalRatings } = useSelector(
    (state) => ({
      ratings: state.pois.ratings,
      poiRating: state.pois.poiRating,
      loading: state.pois.loading,
      poisList: state.pois.list,
      totalRatings: state.pois.totalRatings || 0,
    }),
    shallowEqual
  );

  // Fetch all POI ratings and POI list on mount
  useEffect(() => {
    if (siteId) {
      dispatch(fetchPOIRatings({ siteId, categoryId: "68f0c03e09ed250136264cf0"  }));
      dispatch(fetchPois({ siteId, categoryId: "68f0c03e09ed250136264cf0" })); // Only toilets category
    } else {
      dispatch(fetchPOIRatings());
    }
  }, [dispatch, siteId]);

  // Update chart when ratings change
  useEffect(() => {
    if (selectedPOI && poiRating) {
      // Individual POI rating details
      const labels = poiRating.ratings?.map((r) => {
        const date = new Date(r.timestamp);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'Asia/Singapore'
        });
      }) || [];
      const data = poiRating.ratings?.map((r) => r.rating) || [];

      setChartData({
        labels,
        datasets: [
          {
            label: `${poiRating.poiName || 'POI'} Ratings`,
            data,
            fill: false,
            borderColor: chartColor,
            backgroundColor: chartColor,
            borderWidth: 2,
            pointBackgroundColor: chartColor,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ],
      });
    } else if (ratings && ratings.length > 0) {
      // Summary view - all POIs
      const labels = ratings.map((r) => r.poiName || r.poiId);
      const avgRatings = ratings.map((r) => r.averageRating || 0);

      setChartData({
        labels,
        datasets: [
          {
            label: 'Average Rating',
            data: avgRatings,
            backgroundColor: chartColor,
            borderColor: chartColor,
            borderWidth: 1,
            yAxisID: 'y',
          },
        ],
      });
    }
  }, [ratings, poiRating, selectedPOI]);

  // Handle POI selection
  const handlePOIChange = (poiId) => {
    if (poiId) {
      setSelectedPOI(poiId);
      setCurrentPage(1);
      dispatch(fetchPOIRatingById({
        poiId,
        siteId,
        page: 1,
        limit: pageSize
      }));
    } else {
      setSelectedPOI(null);
      setCurrentPage(1);
      if (siteId) {
        dispatch(fetchPOIRatings({ siteId }));
      } else {
        dispatch(fetchPOIRatings());
      }
    }
  };

  // Handle pagination change
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    if (selectedPOI) {
      dispatch(fetchPOIRatingById({
        poiId: selectedPOI,
        siteId,
        page: pagination.current,
        limit: pagination.pageSize
      }));
    }
  };

  // Calculate summary statistics
  const calculateStats = () => {
    // Use poisList for total ratings count as it contains the correct totalRatings per POI
    const dataSource = poisList && poisList.length > 0 ? poisList : ratings;

    if (!dataSource || dataSource.length === 0) {
      return {
        averageOverall: 0,
        totalRatingsCount: 0,
        topRatedPOI: null,
      };
    }

    const totalRatingsCount = dataSource.reduce(
      (sum, r) => sum + (r.totalRatings || 0),
      0
    );
    const weightedSum = dataSource.reduce(
      (sum, r) => sum + (r.averageRating || 0) * (r.totalRatings || 0),
      0
    );
    const averageOverall =
      totalRatingsCount > 0 ? weightedSum / totalRatingsCount : 0;

    const topRatedPOI = dataSource.reduce((top, current) => {
      if (!top || (current.averageRating || 0) > (top.averageRating || 0)) {
        return current;
      }
      return top;
    }, null);

    return {
      averageOverall: averageOverall.toFixed(2),
      totalRatingsCount,
      topRatedPOI,
    };
  };

  const stats = calculateStats();

  // Chart options - Chart.js v3 syntax
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: selectedPOI ? 10 : undefined,
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
        },
        title: {
          display: true,
          text: selectedPOI ? 'Rating' : 'Average Rating',
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += context.parsed.y;
            if (context.dataset.yAxisID === 'y') {
              label += ' stars';
            }
            return label;
          },
        },
      },
      datalabels: {
        font: {
          size: 0,
        },
      },
    },
  };

  // Table columns for summary view
  const columns = [
    {
      title: 'POI Name',
      dataIndex: 'poiName',
      key: 'poiName',
      sorter: (a, b) => (a.poiName || '').localeCompare(b.poiName || ''),
    },
    {
      title: 'Average Rating',
      dataIndex: 'averageRating',
      key: 'averageRating',
      render: (rating) => (
        <span>
          <StarOutlined style={{ color: '#fadb14', marginRight: 4 }} />
          {rating ? rating.toFixed(2) : '0.00'}
        </span>
      ),
      sorter: (a, b) => (a.averageRating || 0) - (b.averageRating || 0),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Latest Rating',
      dataIndex: 'latestRating',
      key: 'latestRating',
      render: (rating, record) => {
        // Get the latest rating from the ratings array if available
        if (record.ratings && record.ratings.length > 0) {
          const latest = record.ratings.reduce((prev, current) => {
            return new Date(current.timestamp) > new Date(prev.timestamp) ? current : prev;
          });
          return latest.rating || 'N/A';
        }
        return rating || 'N/A';
      },
    },
  ];

  // Table columns for individual POI ratings
  const individualRatingColumns = [
    {
      title: 'Date',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => {
        const ratingDate = new Date(timestamp);
        return ratingDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Asia/Singapore',
        });
      },
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      render: (rating) => (
        <span>
          <StarOutlined style={{ color: '#fadb14', marginRight: 4 }} />
          {rating || 'N/A'}
        </span>
      ),
      sorter: (a, b) => (a.rating || 0) - (b.rating || 0),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (description) => description || 'No description',
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <ReportWrapper>
        {/* <ChartTitle>POI/Tenant Ratings</ChartTitle> */}

        {/* Summary Statistics */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card style={{ minHeight: 112 }}>
              <Statistic
                title="Overall Average Rating"
                value={stats.averageOverall}
                prefix={<StarOutlined />}
                suffix="/ 5"
                precision={2}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card style={{ minHeight: 112 }}>
              <Statistic
                title="Total Ratings Count"
                value={stats.totalRatingsCount}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card style={{ minHeight: 112 }}>
              <Statistic
                title="Top Rated POI"
                value={
                  stats.topRatedPOI
                    ? `${stats.topRatedPOI.name || stats.topRatedPOI.poiName} (${
                        stats.topRatedPOI.averageRating?.toFixed(2) || 'N/A'
                      })`
                    : 'N/A'
                }
                prefix={<TrophyOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* POI Selector */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ marginRight: 8, fontWeight: 'bold' }}>
            Select POI:
          </label>
          <Select
            style={{ width: 300 }}
            placeholder="View all POIs summary"
            allowClear
            onChange={handlePOIChange}
            value={selectedPOI}
          >
            {poisList.map((poi) => (
              <Option key={poi._id} value={poi._id}>
                {poi.name}
              </Option>
            ))}
          </Select>
        </div>

        {/* Chart */}
        <ChartWrapper>
          <div style={{ height: '400px' }}>
            {chartData.labels.length > 0 ? (
              selectedPOI ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <Bar data={chartData} options={chartOptions} />
              )
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '50px',
                  color: '#999',
                }}
              >
                No rating data available
              </div>
            )}
          </div>
        </ChartWrapper>

        {/* Detailed Table */}
        {!selectedPOI && ratings && ratings.length > 0 && (
          <div style={{ marginTop: 40 }}>
            <h3 style={{ marginBottom: 16 }}>Detailed Ratings Table</h3>
            <Table
              columns={columns}
              dataSource={ratings}
              rowKey={(record) => record.poiId || record._id}
              pagination={{ pageSize: 10 }}
            />
          </div>
        )}

        {/* Individual POI Details */}
        {selectedPOI && poiRating && (
          <div style={{ marginTop: 40 }}>
            <h3 style={{ marginBottom: 16 }}>
              Rating Details for {poiRating.poiName}
            </h3>
            <Card>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Average Rating"
                    value={poiRating.averageRating?.toFixed(2) || 0}
                    prefix={<StarOutlined />}
                    suffix="/ 5"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Total Ratings"
                    value={poiRating.totalRatings || 0}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Latest Rating"
                    value={
                      poiRating.ratings && poiRating.ratings.length > 0
                        ? poiRating.ratings.reduce((prev, current) => {
                            return new Date(current.timestamp) > new Date(prev.timestamp) ? current : prev;
                          }).rating
                        : 'N/A'
                    }
                  />
                </Col>
              </Row>
            </Card>

            {/* Individual Ratings Table */}
            {poiRating.ratings && poiRating.ratings.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h4 style={{ marginBottom: 16 }}>All Ratings</h4>
                <Table
                  columns={individualRatingColumns}
                  dataSource={poiRating.ratings}
                  rowKey={(record, index) => record._id || index}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalRatings,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} ratings`,
                    pageSizeOptions: ['20', '50', '100'],
                  }}
                  onChange={handleTableChange}
                />
              </div>
            )}
          </div>
        )}
      </ReportWrapper>
    </div>
  );
};

export default POIRatingChart;
